// ✅ MOBILE SYNC: Shared subscription utilities matching mobile app patterns
import { doc, getDoc, updateDoc, Timestamp, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

// ✅ V2: Helper to add subscription history to subcollection (scalable)
const addSubscriptionHistoryEntry = async (userId, entry) => {
  try {
    const historyRef = collection(db, 'users', userId, 'subscriptionHistory');
    const entryId = `${Date.now()}_${entry.action || 'change'}`;

    await addDoc(historyRef, {
      ...entry,
      createdAt: serverTimestamp(),
      timestamp: entry.timestamp || new Date().toISOString()
    });

    // Update user doc with summary
    await updateDoc(doc(db, 'users', userId), {
      lastSubscriptionChange: {
        action: entry.action,
        planId: entry.planId,
        timestamp: entry.timestamp || new Date().toISOString()
      },
      updatedAt: serverTimestamp()
    });

    return entryId;
  } catch (error) {
    console.error('Error adding subscription history entry:', error);
    // Don't throw - this is supplementary data
  }
};

export const hasUsedFreeTrial = async (userId, userData = null) => {
  try {
    if (!userData) {
      const userDoc = await getDoc(doc(db, "users", userId));
      userData = userDoc.exists() ? userDoc.data() : null;
    }
    
    // Check multiple indicators that user has used free trial like mobile
    return !!(
      userData?.hasUsedFreeTrial || // Explicit flag
      userData?.trialEnd || // Has trial end date
      userData?.trialStartDate // Has trial start date - means they activated a trial
    );
  } catch (error) {
    return true; // Safe default - assume they've used it
  }
};

export const checkTrialEligibility = async (userId, userData = null) => {
  try {
    if (!userData) {
      const userDoc = await getDoc(doc(db, "users", userId));
      userData = userDoc.exists() ? userDoc.data() : null;
    }
    
    // ✅ Check if user has ever used free trial like mobile
    const hasUsedTrial = await hasUsedFreeTrial(userId, userData);
    
    if (hasUsedTrial) {
      return { 
        eligible: false, 
        reason: 'trial_already_used',
        message: 'You have already used your free trial. Please choose a paid plan to continue.'
      };
    }

    // ✅ Check if user currently has an active paid plan like mobile
    if (userData?.planId && userData.planId !== 'free' && userData.planId !== null && userData.planId !== 'none') {
      return { 
        eligible: false, 
        reason: 'has_paid_plan',
        message: 'You already have an active subscription. No need for a trial!'
      };
    }

    // ✅ Check if user is currently on an active trial like mobile
    if (userData?.planId === 'free' && userData?.trialEnd) {
      const trialEndDate = userData.trialEnd.toDate ? userData.trialEnd.toDate() : new Date(userData.trialEnd);
      const now = new Date();
      
      if (trialEndDate > now) {
        return { 
          eligible: false, 
          reason: 'trial_active',
          message: 'Your free trial is already active and will continue until ' + trialEndDate.toLocaleDateString()
        };
      }
    }

    return { eligible: true, reason: 'eligible' };
    
  } catch (error) {
    return { 
      eligible: false, 
      reason: 'error',
      message: 'Unable to verify trial eligibility. Please try again.'
    };
  }
};

export const getTrialStatus = async (userId, userData = null) => {
  try {
    if (!userData) {
      const userDoc = await getDoc(doc(db, "users", userId));
      userData = userDoc.exists() ? userDoc.data() : null;
    }
    
    const hasUsedTrial = await hasUsedFreeTrial(userId, userData);
    
    if (!userData) {
      return {
        status: 'never_started',
        eligible: !hasUsedTrial,
        message: hasUsedTrial ? 'Trial already used' : 'Start your free trial'
      };
    }

    // Current trial active
    if (userData.planId === 'free' && userData.trialEnd) {
      const trialEndDate = userData.trialEnd.toDate ? userData.trialEnd.toDate() : new Date(userData.trialEnd);
      const now = new Date();
      const daysLeft = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysLeft > 0) {
        return {
          status: 'active',
          daysLeft: daysLeft,
          endDate: trialEndDate,
          message: `${daysLeft} days left`
        };
      } else {
        return {
          status: 'expired',
          endDate: trialEndDate,
          message: 'Trial expired'
        };
      }
    }

    // Has paid plan
    if (userData.planId && userData.planId !== 'free') {
      return {
        status: 'upgraded',
        currentPlan: userData.planId,
        message: 'Active subscription'
      };
    }

    // No trial, never used
    return {
      status: 'available',
      eligible: !hasUsedTrial,
      message: hasUsedTrial ? 'Trial already used' : 'Start free trial'
    };

  } catch (error) {
    return null;
  }
};

export const startFreeTrial = async (userId) => {
  try {
    // ✅ MOBILE SYNC: Check eligibility first like mobile
    const eligibility = await checkTrialEligibility(userId);
    
    if (!eligibility.eligible) {
      let title = "Free Trial Not Available";
      let message = eligibility.message || "You are not eligible for a free trial.";
      
      switch (eligibility.reason) {
        case 'trial_already_used':
          title = "Trial Already Used 🎁";
          message = "You've already enjoyed your 30-day free trial! Choose a paid plan to continue using Camyvera.";
          break;
        case 'has_paid_plan':
          title = "Already Subscribed ✅";
          message = "You already have an active subscription. Enjoy your premium access!";
          break;
        case 'trial_active':
          title = "Trial Already Active 🎉";
          break;
      }
      
      return { success: false, title, message };
    }

    // ✅ MOBILE SYNC: Start the trial with comprehensive tracking like mobile
    const now = new Date();
    const trialEnd = Timestamp.fromDate(
      new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000) // 30 days
    );

    // Free plan: 300 minutes every month (first and recurring), all features included
    const updateData = {
      planId: "free",
      trialEnd: trialEnd,
      trialStartDate: Timestamp.fromDate(now),
      hasUsedFreeTrial: true,
      trialMinutesLimit: 300,
      monthlyFreeMinutes: 300,
      isRecurringFreePlan: true,
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, "users", userId), updateData);

    await addSubscriptionHistoryEntry(userId, {
      action: 'free_plan_started',
      planId: 'free',
      timestamp: now.toISOString(),
      trialEnd: trialEnd.toDate().toISOString(),
      monthlyMinutes: 300,
      features: 'Audio + Video',
      source: 'web'
    });
    return { 
      success: true, 
      title: "Free Trial Started! 🎉", 
      message: "Your 30-day free trial has begun. Enjoy full access to Camyvera!"
    };

  } catch (error) {
    return { 
      success: false, 
      title: "Error", 
      message: "Failed to start free trial. Please try again." 
    };
  }
};
